import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    useTheme,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useAppDispatch } from '../../../hooks/reduxHooks'
import { InvoiceManager } from '../../../services/api/InvoiceManager'
import { InvoiceIssuerBriefDto, InvoicePaymentType } from '../../../services/api/sharedTypes/Invoice'
import { displayMessage } from '../../../services/redux/snackbar/snackbar-actions'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import InvoiceServiceSelect from './InvoiceServiceSelect'
import CustomTextField from '../../common/CustomTextField'
import CustomDatePicker from '../../common/CustomDatePicker'
import InvoicePaymentTypeSelect from '../../common/InvoicePaymentTypeSelect'

interface IssuersDialogProps {
    onSend?: (
        invoiceIssuerId: string,
        productsText: string,
        orderNumber: string,
        paymentType: InvoicePaymentType,
        issueDate?: Date,
        taxDate?: Date,
        dueDate?: Date,
    ) => void
    hasContactWithEmail?: boolean
    taxDate: Date
}

const IssuersCreateDialog: React.FC<IssuersDialogProps> = ({
    onSend,
    hasContactWithEmail,
    taxDate: taxDateProp,
}: IssuersDialogProps) => {
    const dispatch = useAppDispatch()
    const theme = useTheme()

    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
    const [loadingPageData, setLoadingPageData] = useState<boolean>(false)
    const [invoiceIssuers, setInvoiceIssuers] = useState<InvoiceIssuerBriefDto[]>([])
    const [selectedInvoiceIssuerId, setSelectedInvoiceIssuerId] = useState<string>('')
    const [selectedServiceText, setSelectedServiceText] = useState<string>('')
    const [orderNumber, setOrderNumber] = useState<string>('')
    const [paymentType, setPaymentType] = useState<InvoicePaymentType>(InvoicePaymentType.NotSet)
    const [issueDate, setIssueDate] = useState<Date | null>(new Date())
    const [dueDate, setDueDate] = useState<Date | null>(null)
    const [taxDate, setTaxDate] = useState<Date | null>(taxDateProp)

    const handleCloseDialog = () => {
        setIsDialogOpen(false)
    }

    const handleOpenDialog = () => {
        if (!onSend)
            return dispatch(
                displayMessage({ message: 'Před vytvářením faktury musíte nejdříve vytvořit událost.', type: 'error' })
            )
        setIsDialogOpen(true)
    }

    useEffect(() => {
        const fetchData = async () => {
            setLoadingPageData(true)
            try {
                const invoiceIssuersRes = await InvoiceManager.getManager().getInvoiceIssuerListAsync()
                if (invoiceIssuersRes) {
                    setInvoiceIssuers(invoiceIssuersRes.invoiceIssuers)
                    setSelectedInvoiceIssuerId(invoiceIssuersRes.invoiceIssuers[0].id)
                    setDueDate(
                        new Date(new Date().setDate(new Date().getDate() + invoiceIssuersRes.invoiceIssuers[0].paymentDays))
                    )
                }
            } catch (error) {
                setLoadingPageData(false)
                return dispatch(displayMessage({ message: error, type: 'error' }))
            }

            setLoadingPageData(false)
        }

        fetchData()
    }, [setLoadingPageData])

    return (
        <>
            <a onClick={handleOpenDialog} style={{ textDecoration: 'underline', cursor: 'pointer', color: 'blue' }}>
                kliknutím zde
            </a>
            <Dialog open={isDialogOpen} onClose={handleCloseDialog}>
                <DialogTitle textAlign='center'>Chcete vytvořit fakturu?</DialogTitle>
                <DialogContent>
                    {!hasContactWithEmail && (
                        <DialogContentText textAlign='center' color={theme.palette.warning.main}>
                            <Stack alignItems='center' mb={2}>
                                <WarningAmberOutlinedIcon />
                                Klient neobsahuje kontakt který by měl emailovou adresu. Nebude proto tedy možné poslat
                                automatický email s fakturou.
                            </Stack>
                        </DialogContentText>
                    )}

                    <DialogContentText>Zvolte na koho chcete fakturovat. (Změna změní datum splatnosti)</DialogContentText>
                    {loadingPageData ? (
                        <CircularProgress />
                    ) : invoiceIssuers ? (
                        <FormControl>
                            <RadioGroup
                                value={selectedInvoiceIssuerId}
                                onChange={(event) => {
                                    setSelectedInvoiceIssuerId(event.target.value)
                                    const issuer = invoiceIssuers.find((issuer) => issuer.id === event.target.value)
                                    setDueDate(new Date(new Date().setDate(new Date().getDate() + (issuer?.paymentDays ?? 0))))
                                }}
                            >
                                {invoiceIssuers.map((issuer) => (
                                    <FormControlLabel key={issuer.id} value={issuer.id} control={<Radio />} label={issuer.name} />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    ) : (
                        <div>Žádní vystavitelé faktur</div>
                    )}

                    <DialogContentText>Zvolte popis služby.</DialogContentText>
                    <InvoiceServiceSelect onChange={setSelectedServiceText} />

                    <DialogContentText>Zvolte způsob platby.</DialogContentText>
                    <InvoicePaymentTypeSelect onChange={setPaymentType} value={paymentType} />

                    <DialogContentText>Zvolte číslo objednávky. (Nepovinné)</DialogContentText>
                    <CustomTextField
                        size='small'
                        label='Číslo objednávky (Nepovinné)'
                        fullWidth
                        style={{ marginTop: theme.spacing(1), marginBottom: theme.spacing(2) }}
                        value={orderNumber}
                        onChange={(e) => setOrderNumber(e.target.value)}
                    />

                    <DialogContentText>Zvolte data. (Nechte prázné data co chcete vyplnit automaticky)</DialogContentText>
                    <Stack direction={'row'} spacing={3} style={{ marginTop: theme.spacing(1) }}>
                        <CustomDatePicker onChange={(value) => setIssueDate(value)} value={issueDate} label='Datum vystavení' />
                        <CustomDatePicker onChange={(value) => setDueDate(value)} value={dueDate} label='Datum splatnosti' />
                        <CustomDatePicker onChange={(value) => setTaxDate(value)} value={taxDate} label='Datum zdan. plnění' />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} variant='outlined'>
                        Zrušit
                    </Button>
                    <Button
                        onClick={() =>
                            onSend &&
                            onSend(
                                selectedInvoiceIssuerId,
                                selectedServiceText,
                                orderNumber,
                                paymentType,
                                issueDate ?? undefined,
                                taxDate ?? undefined,
                                dueDate ?? undefined
                            )
                        }
                        variant='contained'
                    >
                        Vytvořit fakturu
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default IssuersCreateDialog
